import * as path from 'shared/path';
import { AsyncSubject, Observable } from 'common/barrels/rx';
import { disposableSubscription, IDisposable, IEventAggregator } from 'common/barrels/core';
import { IResourceProvider } from 'common/resourceprovider';
import { Request } from 'common/request';
import { Serializer } from 'common/serializer';
import { div, hr, icon, label, li, ul } from 'common/barrels/dom';
import { Screen } from './screen'; 
import { NotificationProcessingResult, ProcessingResult } from 'runtime/messages/notificationprocessingresult';
import { NotBusy } from 'runtime/events/notbusy';
import { ValidationItem, ValidationSummary } from 'shared/data/validation';

import 'runtime/styles/screens/processnotification.less';

export class ProcessNotificationResponse extends Screen { 
    private trackingId: string;
    private response: string;
    private asyncSubjectSubscription: IDisposable; 
    
    constructor($hostElement: JQuery, resourcesObservable: Observable<IResourceProvider>, parentEventAggregator: IEventAggregator, 
                serializer: Serializer, notificationParams: string[], csrfToken: () => string) {
        super($hostElement, resourcesObservable, parentEventAggregator, serializer, csrfToken); 
        
        this.$element = div('wf-container wf-processnotification-container').appendTo($hostElement)
            .on('remove', () => this.dispose());
        
        // Initialize trackingId and response as BehaviorSubjects
        this.trackingId = notificationParams[0] || '';
        this.response = notificationParams[1] || '';

        var subject = new AsyncSubject<NotificationProcessingResult>();
        this.asyncSubjectSubscription = disposableSubscription(
            this.resourcesAvailable.zip(subject, (v1, v2) => v2).subscribe(result => {
                this.buildContainer(this.$element, this.resources, result);
            })
        );

        if(!this.trackingId) {
            var result = new NotificationProcessingResult();
            result.result = ProcessingResult.MissingTrackingId;
            this.buildContainer(this.$element, this.resources, result);
            this.eventAggregator.publish(new NotBusy());
            return;
        }

        Request.create(serializer, path.api(`notification/process/${this.trackingId}/${this.response}`))
            .handle(NotificationProcessingResult, value => (subject.next(value), subject.complete()))
            .error((jqXhr, textStatus, errorThrown) => {
                this.httpError(errorThrown, textStatus);
            })
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .get();
    }

    private buildContainer($element: JQuery, resources: IResourceProvider, result: NotificationProcessingResult): void {
        const $container = div('processnotification-content').appendTo($element);
        const $header = div('processnotification-header').appendTo($container);
        $header.append([
            div().text(resources.getCoreString('NotificationProcessingResultTitle')),
            hr()
        ]);
        
        const $body = div('processnotification-details').appendTo($container);
        const messageText = this.getNotificationMessageText(result, resources);
        $body.append(div().append([getIcon(result.IsSuccessful()), label().text(messageText)]));
        
        if ((result.result === ProcessingResult.ValidationFailed || result.result === ProcessingResult.FailedValidationCheck) && result.validationSummary) {
            renderValidationResults(result.validationSummary, $body);
        }

        function getIcon(isSuccessful: boolean): JQuery {
            const iconName = isSuccessful ? 'check' : 'times';
            return icon(iconName, '', 'processnotification-icon');
        }

        function renderValidationResults(summary: ValidationSummary, $parent: JQuery): void {
            const $ul = ul().appendTo($parent);
            summary.validationItems.forEach(item => {
                renderValidationResult(item, $ul);
            });
        }
        
        function renderValidationResult(item: ValidationItem, $parent: JQuery): void {
            const $li = li().text(item.message).appendTo($parent);
            if (item instanceof ValidationSummary) {
                const $ul = ul().appendTo($li);
                item.validationItems.forEach(subItem => {
                    renderValidationResult(subItem, $ul);
                });
            }
        }
    }

    private getNotificationMessageText(result: NotificationProcessingResult, resources: IResourceProvider): string {
        let message = '';
        switch (result.result) {
            case 'processingSuccessful':
                message = `${resources.getCoreString('NotificationResultProcessingSuccessfulExisting')
                    .replace('{0}', this.response)}`;
                break;
            case 'accessDenied':
                message = `${resources.getCoreString('NotificationResultAccessDenied')}`;
                break;
            case 'addressMismatch':
                message = `${resources.getCoreString('NotificationResultAddressMismatch')}`;
                break;
            case 'alreadyProcessed':
                message = `${resources.getCoreString('NotificationResultAlreadyProcessed')}`;
                break;
            case 'configurationError':
                message = `${resources.getCoreString('NotificationResultAlreadyProcessed')}`;
                break;
            case 'failedValidationCheck':
                message = `${resources.getCoreString('NotificationResultFailedValidationCheck').replace('{0}', '')}`;
                break;
            case 'missingOrAmbiguousMessageType':
                message = `${resources.getCoreString('')}`;
                break;
            case 'missingOrAmbiguousResponseField':
                message = `${resources.getCoreString('')}`;
                break;
            case 'missingTrackingId':
                message = `${resources.getCoreString('NotificationResultMissingTrackingId')}`;
                break;
            case 'noActions':
                message = `${resources.getCoreString('NotificationResultNoActions')}`;
                break;
            case 'otherBatchItemsFailed':
                message = `${resources.getCoreString('NotificationResultOtherBatchItemsFailed')}`;
                break;
            case 'responseDoesNotSupportNewItems':
                message = `${resources.getCoreString('')}`;
                break;
            case 'unknownMessageType':
                message = `${resources.getCoreString('')}`;
                break;
            case 'unknownTrackingId':
                message = `${resources.getCoreString('NotificationResultUnknownTrackingId')}`;
                break;
            case 'unknownUser':
                message = `${resources.getCoreString('NotificationResultUnknownUser')}`;
                break;
            case 'unknownWorkItemError':
                message = `${resources.getCoreString('NotificationResultUnknownWorkItemError')}`;
                break;
            case 'unrecognizedResponse':
                message = `${resources.getCoreString('NotificationResultUnrecognizedResponse')
                    .replace('{0}', result.validResponses?.join(', '))}`;
                break;
            case 'unsupportedBatchOperation':
                message = `${resources.getCoreString('NotificationResultUnsupportedBatchOperation')}`;
                break;
            case 'validationFailed':
                message = `${resources.getCoreString('NotificationResultValidationFailed').replace('{0}', '')}`;
                break;
            case 'workItemNotAvailableForClaim':
                message = `${resources.getCoreString('')}`;
                break;
            case 'workItemClaimedByAnotherUser':
                message = `${resources.getCoreString('NotificationResultWorkItemNotAvailableBecauseClaimed')}`;
                break;
            case 'workItemProgressedToNextStep':
                message = `${resources.getCoreString('NotificationResultWorkItemNotAvailableBecauseProgressed')}`;
                break;
            default:
                message = `${resources.getCoreString('NotificationResultSystemError')}`;
                break;
        }
        return message;
    }

    dispose(): void {
        this.asyncSubjectSubscription.dispose();
    }
}